import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { Button } from '../../../../components/ui/button';
import { IOnboardingDocument, IOnboardingTemplateType } from 'core/interfaces/onboardingInterface';
import { updateOnboardingDocument, getOnboardingTemplateTypes } from 'core/API/onboarding';
import { validateDocumentFile } from 'module/onboarding/utils/onboardingValidation';
import styles from '../SchedulerSetting.module.scss';

interface EditDocumentProps {
  document: IOnboardingDocument;
  existingNames: string[];
  handleBackClick?: () => void;
  onDocumentUpdated?: () => void;
}

const EditDocument: React.FC<EditDocumentProps> = ({
  document,
  existingNames: _existingNames,
  handleBackClick,
  onDocumentUpdated,
}) => {
  // Extract type ID from IRI (e.g., "/api/onboarding_template_types/1" -> 1)
  const getTypeIdFromIRI = (iri: string): number => {
    const match = iri.match(/\/(\d+)$/);
    return match ? Number(match[1]) : 1;
  };

  const [documentBuffer, setDocumentBuffer] = useState({
    name: document.name,
    typeId: getTypeIdFromIRI(document.type),
    sortOrder: document.sortOrder,
    isManagerSignatureRequired: document.isManagerSignatureRequired || false,
  });

  const [templateTypes, setTemplateTypes] = useState<IOnboardingTemplateType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validatePdfFile = (file: File): string | null => {
    const validationErrors = validateDocumentFile({
      type: file.type,
      size: file.size,
      name: file.name,
    });
    return validationErrors[0] ?? null;
  };

  const handleFileAccepted = (file: File) => {
    const validationError = validatePdfFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  useEffect(() => {
    loadTemplateTypes();
  }, []);

  const loadTemplateTypes = async () => {
    try {
      setIsLoadingTypes(true);
      const types = await getOnboardingTemplateTypes();
      setTemplateTypes(types.filter((t) => t.active !== false));
    } catch (err) {
      console.error('Error loading template types:', err);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const handleDocumentTypeChange = (event: SelectChangeEvent<number>) => {
    setDocumentBuffer((prev) => ({
      ...prev,
      typeId: Number(event.target.value),
    }));
    setError(null);
  };

  const handleSortOrderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 50) {
      setDocumentBuffer((prev) => ({
        ...prev,
        sortOrder: value,
      }));
      setError(null);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileAccepted(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleFileAccepted(file);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    // Validate
    if (!documentBuffer.name.trim()) {
      setError('Name is required');
      return;
    }

    if (documentBuffer.sortOrder < 1 || documentBuffer.sortOrder > 50) {
      setError('Sort order must be between 1 and 50');
      return;
    }

    if (!document.id) {
      setError('Document ID is missing');
      return;
    }

    if (selectedFile) {
      const fileValidationError = validatePdfFile(selectedFile);
      if (fileValidationError) {
        setError(fileValidationError);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Build payload
      const payload: any = {
        name: documentBuffer.name.trim(),
        sortOrder: documentBuffer.sortOrder,
        isManagerSignatureRequired: documentBuffer.isManagerSignatureRequired,
        workplace: document.workplace,
        type: `/api/onboarding_template_types/${documentBuffer.typeId}`,
      };
      
      await updateOnboardingDocument(
        document.id,
        payload,
        selectedFile || undefined
      );

      setSuccess('Document updated successfully!');
      
      // Call parent callback after a brief delay to show success message
      setTimeout(() => {
        if (onDocumentUpdated) {
          onDocumentUpdated();
        }
        if (handleBackClick) {
          handleBackClick();
        }
      }, 1500);
    } catch (err: any) {
      console.error('Error updating document:', err);
      setError(err.message || 'Failed to update document');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.editGeneralSettings}>
      <Grid container direction="column" spacing={2} pl={2}>
        <Grid container direction="row" justifyContent="space-between">
          <Grid>
            <Button className={styles.mabelButton} onClick={handleBackClick}>
              Back
            </Button>
          </Grid>
          <Grid item xs={9} display={'flex'} justifyContent="flex-end">
            <Grid>
              <Button className={styles.mabelButton} onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <h1 className={styles.formTitle}>Edit Onboarding Document</h1>
        </Grid>
      </Grid>

      <Box sx={{ padding: 3 }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {success && (
          <Typography color="success.main" sx={{ mb: 2 }}>
            {success}
          </Typography>
        )}

        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current File Path:
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
            {document.filePath || 'N/A'}
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Replace PDF File (Optional):
          </Typography>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Upload replacement PDF — click or drag and drop"
            sx={{
              border: isDragging
                ? '2px dashed #446388'
                : selectedFile
                  ? '2px dashed #4caf50'
                  : '2px dashed #ccc',
              borderRadius: 1,
              padding: 2,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragging ? '#eef2f7' : '#fafafa',
              mt: 1,
              transition: 'border-color 0.2s, background-color 0.2s',
            }}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              aria-label="Select replacement PDF file"
            />
            <CloudUpload sx={{ fontSize: 40, color: '#999', mb: 1 }} />
            <Typography variant="body2">
              {selectedFile ? selectedFile.name : 'Click or drag & drop a new PDF file here'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Maximum file size: 10MB | Format: PDF only
            </Typography>
          </Box>
        </Box>

        <TextField
          label="Name"
          value={documentBuffer.name}
          onChange={(e) => {
            setDocumentBuffer((prev) => ({
              ...prev,
              name: e.target.value,
            }));
            setError(null);
          }}
          fullWidth
          required
          helperText="Document name shown to employees"
          sx={{ mb: 3 }}
          inputProps={{
            maxLength: 100,
          }}
        />

        <FormControl fullWidth sx={{ mb: 3 }} required>
          <InputLabel>Document Type</InputLabel>
          <Select
            value={documentBuffer.typeId}
            onChange={handleDocumentTypeChange}
            label="Document Type"
            disabled={isLoadingTypes || isSubmitting}
          >
            {templateTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -2, mb: 3, display: 'block' }}>
          Select the type of PDF document
        </Typography>

        <TextField
          label="Sort Order"
          type="number"
          value={documentBuffer.sortOrder}
          onChange={handleSortOrderChange}
          fullWidth
          required
          helperText="Order in which the document appears (1-50)"
          sx={{ mb: 3 }}
          inputProps={{
            min: 1,
            max: 50,
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={documentBuffer.isManagerSignatureRequired}
              onChange={(e) => {
                setDocumentBuffer((prev) => ({
                  ...prev,
                  isManagerSignatureRequired: e.target.checked,
                }));
                setError(null);
              }}
              disabled={isSubmitting}
            />
          }
          label="Require Manager Signature"
          sx={{ mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 3 }}>
          Check this if manager signature is required for this document
        </Typography>
      </Box>
    </div>
  );
};

export default EditDocument;
